/**
 * Thin fetch wrapper. Uses HTTP-only cookies for auth, echoes the CSRF cookie in
 * X-CSRF-Token for state-changing requests, and transparently refreshes an expired
 * access token once before failing.
 */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: { path: string; message: string }[] | any) {
    super(message);
  }
}

export interface ApiResult<T> {
  data: T;
  meta?: { page: number; limit: number; total: number; totalPages: number; [k: string]: unknown };
}

const BASE = import.meta.env.VITE_API_BASE ?? '';

function csrfToken() {
  const m = document.cookie.match(/(?:^|; )ylm_csrf=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : '';
}

let refreshing: Promise<boolean> | null = null;
async function tryRefresh() {
  refreshing ??= fetch(`${BASE}/api/auth/refresh`, { method: 'POST', credentials: 'include', headers: { 'X-CSRF-Token': csrfToken() } })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => setTimeout(() => (refreshing = null), 0));
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  form?: FormData;
  signal?: AbortSignal;
  retry?: boolean;
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<ApiResult<T>> {
  const method = opts.method ?? (opts.body || opts.form ? 'POST' : 'GET');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-CSRF-Token'] = csrfToken();
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
    signal: opts.signal,
  });
  if (res.status === 401 && path.startsWith('/api/admin') && opts.retry !== false) {
    if (await tryRefresh()) return request<T>(path, { ...opts, retry: false });
    window.dispatchEvent(new CustomEvent('ylm:unauthorized'));
  }
  let json: any = null;
  try {
    json = await res.json();
  } catch {
    /* non-JSON */
  }
  if (!res.ok || json?.success === false) {
    const err = json?.error ?? {};
    throw new ApiError(res.status, err.code ?? 'HTTP_ERROR', err.message ?? `Request failed (${res.status})`, err.details);
  }
  return { data: json?.data as T, meta: json?.meta };
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ?? {} }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body: body ?? {} }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', form }),
};

export function qs(params: Record<string, unknown>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** Maps API validation details onto a { path: message } object for forms. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || !Array.isArray(err.details)) return {};
  return Object.fromEntries(err.details.map((d: any) => [d.path, d.message]));
}

export function errorMessage(err: unknown) {
  if (err instanceof ApiError) {
    if (Array.isArray(err.details) && err.details.length) return `${err.message}: ${err.details.slice(0, 3).map((d: any) => (d.path ? `${d.path} – ${d.message}` : d.message)).join('; ')}`;
    return err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}
